import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuthStore } from '../store/auth';
import { api } from '../services/api';
import { PageHeader } from '../components/PageHeader';
import { Layout } from '../components/Layout';

interface Tenant {
  id: string;
  tenant_id: string;
  name: string;
  slug: string;
  logo_url: string | null;
  primary_color: string;
  is_active: boolean;
  street: string | null;
  zip_code: string | null;
  city: string | null;
  country: string | null;
  phone: string | null;
  email: string | null;
  website: string | null;
  tax_id: string | null;
  vat_id: string | null;
  meta_app_id: string | null;
  meta_app_secret_encrypted: string | null;
  meta_access_token_encrypted: string | null;
  meta_token_expires_at: string | null;
  created_at: string;
  updated_at: string;
}

interface TenantFormData {
  name: string;
  slug: string;
  logo_url: string;
  primary_color: string;
  street: string;
  zip_code: string;
  city: string;
  country: string;
  phone: string;
  email: string;
  website: string;
  tax_id: string;
  vat_id: string;
  meta_app_id: string;
  meta_app_secret: string;
  meta_access_token: string;
}

function TenantModal({
  isOpen,
  onClose,
  tenant,
  onSave,
}: {
  isOpen: boolean;
  onClose: () => void;
  tenant: Tenant | null;
  onSave: (data: TenantFormData) => Promise<void>;
}) {
  const { t } = useTranslation(['tenants', 'common']);
  const emptyFormData: TenantFormData = {
    name: '',
    slug: '',
    logo_url: '',
    primary_color: '#3B82F6',
    street: '',
    zip_code: '',
    city: '',
    country: t('tenants:address.defaultCountry'),
    phone: '',
    email: '',
    website: '',
    tax_id: '',
    vat_id: '',
    meta_app_id: '',
    meta_app_secret: '',
    meta_access_token: '',
  };

  const [formData, setFormData] = useState<TenantFormData>(emptyFormData);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showSecrets, setShowSecrets] = useState({ appSecret: false, accessToken: false });

  useEffect(() => {
    if (tenant) {
      setFormData({
        name: tenant.name || '',
        slug: tenant.slug || '',
        logo_url: tenant.logo_url || '',
        primary_color: tenant.primary_color || '#3B82F6',
        street: tenant.street || '',
        zip_code: tenant.zip_code || '',
        city: tenant.city || '',
        country: tenant.country || t('tenants:address.defaultCountry'),
        phone: tenant.phone || '',
        email: tenant.email || '',
        website: tenant.website || '',
        tax_id: tenant.tax_id || '',
        vat_id: tenant.vat_id || '',
        meta_app_id: tenant.meta_app_id || '',
        // Secrets werden nicht vom Server geladen - nur neu gesetzt
        meta_app_secret: '',
        meta_access_token: '',
      });
    } else {
      setFormData(emptyFormData);
    }
    setError(null);
    setShowSecrets({ appSecret: false, accessToken: false });
  }, [tenant, isOpen, t]);

  if (!isOpen) return null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.name.trim()) {
      setError(t('tenants:validation.nameRequired'));
      return;
    }

    setIsSubmitting(true);
    setError(null);
    try {
      await onSave(formData);
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : t('tenants:errors.generic'));
    } finally {
      setIsSubmitting(false);
    }
  };

  const generateSlug = (name: string) => {
    return name
      .toLowerCase()
      .replace(/[äöüß]/g, c => ({ 'ä': 'ae', 'ö': 'oe', 'ü': 'ue', 'ß': 'ss' })[c] || c)
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-|-$/g, '');
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="bg-gradient-to-r from-blue-600 to-indigo-600 px-6 py-4 rounded-t-lg">
          <h2 className="text-xl font-semibold text-white">
            {tenant ? t('tenants:editTenant') : t('tenants:newTenant')}
          </h2>
          {tenant && (
            <p className="text-blue-100 text-sm font-mono">{tenant.tenant_id}</p>
          )}
        </div>

        {/* Error */}
        {error && (
          <div className="px-6 py-3 bg-red-50 text-red-700 text-sm">{error}</div>
        )}

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6">
          {/* Grunddaten */}
          <h4 className="text-sm font-semibold text-gray-900 uppercase tracking-wider mb-3 flex items-center gap-2">
            <BuildingIcon className="w-4 h-4 text-gray-400" />
            {t('tenants:sections.companyData')}
          </h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t('tenants:fields.nameRequired')} <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => {
                  setFormData({
                    ...formData,
                    name: e.target.value,
                    slug: !tenant ? generateSlug(e.target.value) : formData.slug
                  });
                }}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">{t('tenants:fields.slug')}</label>
              <input
                type="text"
                value={formData.slug}
                onChange={(e) => setFormData({ ...formData, slug: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 font-mono text-sm"
                placeholder={t('tenants:fields.slugAuto')}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">{t('tenants:fields.primaryColor')}</label>
              <div className="flex gap-2">
                <input
                  type="color"
                  value={formData.primary_color}
                  onChange={(e) => setFormData({ ...formData, primary_color: e.target.value })}
                  className="w-12 h-10 p-1 border border-gray-300 rounded-lg cursor-pointer"
                />
                <input
                  type="text"
                  value={formData.primary_color}
                  onChange={(e) => setFormData({ ...formData, primary_color: e.target.value })}
                  className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 font-mono text-sm"
                  pattern="^#[0-9A-Fa-f]{6}$"
                />
              </div>
            </div>

            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">{t('tenants:fields.logoUrl')}</label>
              <input
                type="url"
                value={formData.logo_url}
                onChange={(e) => setFormData({ ...formData, logo_url: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="https://..."
              />
            </div>
          </div>

          {/* Kontakt */}
          <h4 className="text-sm font-semibold text-gray-900 uppercase tracking-wider mb-3 pt-4 border-t border-gray-200 flex items-center gap-2">
            <MailIcon className="w-4 h-4 text-gray-400" />
            {t('tenants:sections.contact')}
          </h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">{t('tenants:contact.email')}</label>
              <input
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">{t('tenants:contact.phone')}</label>
              <input
                type="tel"
                value={formData.phone}
                onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder={t('tenants:contact.phonePlaceholder')}
              />
            </div>
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">{t('tenants:contact.website')}</label>
              <input
                type="url"
                value={formData.website}
                onChange={(e) => setFormData({ ...formData, website: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder={t('tenants:contact.websitePlaceholder')}
              />
            </div>
          </div>

          {/* Adresse */}
          <h4 className="text-sm font-semibold text-gray-900 uppercase tracking-wider mb-3 pt-4 border-t border-gray-200 flex items-center gap-2">
            <MapPinIcon className="w-4 h-4 text-gray-400" />
            {t('tenants:sections.address')}
          </h4>
          <div className="grid grid-cols-4 md:grid-cols-12 gap-4 mb-6">
            <div className="col-span-4 md:col-span-12">
              <label className="block text-sm font-medium text-gray-700 mb-1">{t('tenants:address.street')}</label>
              <input
                type="text"
                value={formData.street}
                onChange={(e) => setFormData({ ...formData, street: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div className="col-span-1 md:col-span-3">
              <label className="block text-sm font-medium text-gray-700 mb-1">{t('tenants:address.postalCode')}</label>
              <input
                type="text"
                value={formData.zip_code}
                onChange={(e) => setFormData({ ...formData, zip_code: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div className="col-span-2 md:col-span-5">
              <label className="block text-sm font-medium text-gray-700 mb-1">{t('tenants:address.city')}</label>
              <input
                type="text"
                value={formData.city}
                onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div className="col-span-1 md:col-span-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">{t('tenants:address.country')}</label>
              <input
                type="text"
                value={formData.country}
                onChange={(e) => setFormData({ ...formData, country: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>

          {/* Steuerdaten */}
          <h4 className="text-sm font-semibold text-gray-900 uppercase tracking-wider mb-3 pt-4 border-t border-gray-200 flex items-center gap-2">
            <DocumentIcon className="w-4 h-4 text-gray-400" />
            {t('tenants:sections.taxData')}
          </h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">{t('tenants:tax.taxId')}</label>
              <input
                type="text"
                value={formData.tax_id}
                onChange={(e) => setFormData({ ...formData, tax_id: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">{t('tenants:tax.vatId')}</label>
              <input
                type="text"
                value={formData.vat_id}
                onChange={(e) => setFormData({ ...formData, vat_id: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder={t('tenants:tax.vatIdPlaceholder')}
              />
            </div>
          </div>

          {/* Meta Platform Integration */}
          <h4 className="text-sm font-semibold text-gray-900 uppercase tracking-wider mb-3 pt-4 border-t border-gray-200 flex items-center gap-2">
            <MetaIcon className="w-4 h-4 text-gray-400" />
            {t('tenants:sections.metaPlatform')}
          </h4>
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
            <p className="text-sm text-blue-800">
              {t('tenants:meta.infoText')}{' '}
              <a href="https://developers.facebook.com" target="_blank" rel="noopener noreferrer" className="underline font-medium">
                {t('tenants:meta.developerPortal')}
              </a>.
            </p>
          </div>
          <div className="space-y-4 mb-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">{t('tenants:meta.appId')}</label>
              <input
                type="text"
                value={formData.meta_app_id}
                onChange={(e) => setFormData({ ...formData, meta_app_id: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 font-mono text-sm"
                placeholder={t('tenants:meta.appIdPlaceholder')}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">{t('tenants:meta.appSecret')}</label>
              <div className="relative">
                <input
                  type={showSecrets.appSecret ? 'text' : 'password'}
                  value={formData.meta_app_secret}
                  onChange={(e) => setFormData({ ...formData, meta_app_secret: e.target.value })}
                  className="w-full px-3 py-2 pr-10 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 font-mono text-sm"
                  placeholder={tenant?.meta_app_secret_encrypted ? `••••••••• ${t('tenants:meta.appSecretAlreadySet')}` : t('tenants:meta.appSecretPlaceholder')}
                />
                <button
                  type="button"
                  onClick={() => setShowSecrets(s => ({ ...s, appSecret: !s.appSecret }))}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  <EyeIcon show={showSecrets.appSecret} />
                </button>
              </div>
              {tenant?.meta_app_secret_encrypted && (
                <p className="text-xs text-gray-500 mt-1">{t('tenants:meta.keepExisting')}</p>
              )}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">{t('tenants:meta.accessToken')}</label>
              <div className="relative">
                <input
                  type={showSecrets.accessToken ? 'text' : 'password'}
                  value={formData.meta_access_token}
                  onChange={(e) => setFormData({ ...formData, meta_access_token: e.target.value })}
                  className="w-full px-3 py-2 pr-10 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 font-mono text-sm"
                  placeholder={tenant?.meta_access_token_encrypted ? `••••••••• ${t('tenants:meta.accessTokenAlreadySet')}` : t('tenants:meta.accessTokenPlaceholder')}
                />
                <button
                  type="button"
                  onClick={() => setShowSecrets(s => ({ ...s, accessToken: !s.accessToken }))}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  <EyeIcon show={showSecrets.accessToken} />
                </button>
              </div>
              {tenant?.meta_access_token_encrypted && (
                <p className="text-xs text-gray-500 mt-1">{t('tenants:meta.keepExisting')}</p>
              )}
              {tenant?.meta_token_expires_at && (
                <p className="text-xs text-amber-600 mt-1">
                  {t('tenants:meta.tokenExpiry')} {new Date(tenant.meta_token_expires_at).toLocaleDateString('de-DE')}
                </p>
              )}
            </div>
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-3 pt-4 border-t border-gray-200">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 text-sm font-medium"
            >
              {t('common:cancel')}
            </button>
            <button
              type="submit"
              disabled={isSubmitting}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 text-sm font-medium"
            >
              {isSubmitting ? t('tenants:saving') : t('common:save')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function TenantCard({
  tenant,
  onEdit,
  onDeactivate,
  isAdmin,
}: {
  tenant: Tenant;
  onEdit: (tenant: Tenant) => void;
  onDeactivate: (id: string) => void;
  isAdmin: boolean;
}) {
  const { t } = useTranslation(['tenants', 'common']);
  const hasMetaIntegration = tenant.meta_app_id && tenant.meta_access_token_encrypted;

  return (
    <div className={`bg-white rounded-xl shadow-sm border border-gray-200 p-5 ${!tenant.is_active ? 'opacity-60' : ''}`}>
      <div className="flex items-start justify-between mb-4">
        <div className="flex items-center gap-3">
          {tenant.logo_url ? (
            <img
              src={tenant.logo_url}
              alt={tenant.name}
              className="w-12 h-12 rounded-lg object-cover"
            />
          ) : (
            <div
              className="w-12 h-12 rounded-lg flex items-center justify-center text-white font-bold text-lg"
              style={{ backgroundColor: tenant.primary_color }}
            >
              {tenant.name.charAt(0).toUpperCase()}
            </div>
          )}
          <div>
            <h3 className="font-semibold text-gray-900">{tenant.name}</h3>
            <p className="text-sm text-gray-500 font-mono">{tenant.tenant_id}</p>
          </div>
        </div>
        <div className="flex flex-col items-end gap-1">
          <span
            className={`px-2 py-1 text-xs rounded-full ${
              tenant.is_active ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
            }`}
          >
            {tenant.is_active ? t('tenants:status.active') : t('tenants:status.inactive')}
          </span>
          {hasMetaIntegration && (
            <span className="px-2 py-1 text-xs rounded-full bg-blue-100 text-blue-800 flex items-center gap-1">
              <MetaIcon className="w-3 h-3" />
              Meta
            </span>
          )}
        </div>
      </div>

      <div className="space-y-2 text-sm text-gray-600 mb-4">
        {tenant.email && (
          <div className="flex items-center gap-2">
            <MailIcon className="w-4 h-4 text-gray-400" />
            <a href={`mailto:${tenant.email}`} className="hover:text-blue-600">{tenant.email}</a>
          </div>
        )}
        {tenant.phone && (
          <div className="flex items-center gap-2">
            <PhoneIcon className="w-4 h-4 text-gray-400" />
            {tenant.phone}
          </div>
        )}
        {(tenant.city || tenant.country) && (
          <div className="flex items-center gap-2">
            <MapPinIcon className="w-4 h-4 text-gray-400" />
            {[tenant.city, tenant.country].filter(Boolean).join(', ')}
          </div>
        )}
        {tenant.website && (
          <div className="flex items-center gap-2">
            <GlobeIcon className="w-4 h-4 text-gray-400" />
            <a href={tenant.website} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline truncate">
              {tenant.website.replace(/^https?:\/\//, '')}
            </a>
          </div>
        )}
      </div>

      <div className="flex items-center justify-between pt-3 border-t border-gray-100">
        <div className="flex items-center gap-2">
          <div
            className="w-4 h-4 rounded border border-gray-200"
            style={{ backgroundColor: tenant.primary_color }}
            title={tenant.primary_color}
          />
          <span className="text-xs text-gray-400 font-mono">{tenant.slug}</span>
        </div>
        <div className="flex gap-2">
          <button
            onClick={() => onEdit(tenant)}
            className="px-3 py-1 text-sm text-blue-600 hover:bg-blue-50 rounded-lg"
          >
            {t('common:edit')}
          </button>
          {isAdmin && tenant.is_active && (
            <button
              onClick={() => onDeactivate(tenant.id)}
              className="px-3 py-1 text-sm text-red-600 hover:bg-red-50 rounded-lg"
            >
              {t('tenants:deactivate')}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

export function TenantsPage() {
  const { t } = useTranslation(['tenants', 'common']);
  const { user } = useAuthStore();
  const [tenants, setTenants] = useState<Tenant[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingTenant, setEditingTenant] = useState<Tenant | null>(null);
  const [searchQuery, setSearchQuery] = useState('');

  const isAdmin = user?.role === 'admin';

  const fetchTenants = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await api.get<{ success: boolean; data: Tenant[] }>('/tenants');
      setTenants(response.data);
    } catch (err) {
      setError(err instanceof Error ? err.message : t('tenants:errors.loadFailed'));
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchTenants();
  }, []);

  const handleCreate = async (data: TenantFormData) => {
    await api.post('/tenants', data);
    await fetchTenants();
  };

  const handleUpdate = async (data: TenantFormData) => {
    if (!editingTenant) return;
    // Only send secrets if they were changed
    const updateData: Record<string, unknown> = { ...data };
    if (!data.meta_app_secret) delete updateData.meta_app_secret;
    if (!data.meta_access_token) delete updateData.meta_access_token;

    await api.put(`/tenants/${editingTenant.id}`, updateData);
    await fetchTenants();
  };

  const handleDeactivate = async (id: string) => {
    if (!confirm(t('tenants:confirmDeactivate'))) return;
    await api.delete(`/tenants/${id}`);
    await fetchTenants();
  };

  const filteredTenants = tenants.filter(t =>
    t.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    t.tenant_id.toLowerCase().includes(searchQuery.toLowerCase()) ||
    t.slug.toLowerCase().includes(searchQuery.toLowerCase()) ||
    (t.email && t.email.toLowerCase().includes(searchQuery.toLowerCase()))
  );

  return (
    <Layout>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <PageHeader
          icon={<BuildingIcon className="w-7 h-7" />}
          title={t('tenants:title')}
          subtitle={t('tenants:subtitle')}
          breadcrumbs={[{ label: t('common:nav.dashboard'), href: '/' }]}
          actions={
            <button
              onClick={() => {
                setEditingTenant(null);
                setIsModalOpen(true);
              }}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center gap-2 text-sm font-medium"
            >
              <PlusIcon className="w-5 h-5" />
              {t('tenants:newTenant')}
            </button>
          }
        />

        {/* Search */}
        <div className="mb-6">
          <div className="relative">
            <SearchIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder={t('tenants:searchPlaceholder')}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
            <div className="text-2xl font-bold text-gray-900">{tenants.length}</div>
            <div className="text-sm text-gray-500">{t('tenants:stats.total')}</div>
          </div>
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
            <div className="text-2xl font-bold text-green-600">{tenants.filter(t => t.is_active).length}</div>
            <div className="text-sm text-gray-500">{t('tenants:stats.active')}</div>
          </div>
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
            <div className="text-2xl font-bold text-blue-600">{tenants.filter(t => t.meta_app_id && t.meta_access_token_encrypted).length}</div>
            <div className="text-sm text-gray-500">{t('tenants:stats.withMeta')}</div>
          </div>
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
            <div className="text-2xl font-bold text-gray-400">{tenants.filter(t => !t.is_active).length}</div>
            <div className="text-sm text-gray-500">{t('tenants:stats.inactive')}</div>
          </div>
        </div>

        {/* Error */}
        {error && (
          <div className="mb-6 px-4 py-3 bg-red-50 text-red-700 rounded-lg border border-red-200">{error}</div>
        )}

        {/* Content */}
        {isLoading ? (
          <div className="text-center py-12 text-gray-500">{t('common:loading')}</div>
        ) : filteredTenants.length === 0 ? (
          <div className="text-center py-12 bg-white rounded-xl shadow-sm border border-gray-200">
            <BuildingIcon className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-sm font-medium text-gray-900">
              {searchQuery ? t('tenants:empty.titleSearch') : t('tenants:empty.title')}
            </h3>
            <p className="mt-1 text-sm text-gray-500">
              {searchQuery ? t('tenants:empty.descriptionSearch') : t('tenants:empty.description')}
            </p>
            {!searchQuery && (
              <button
                onClick={() => setIsModalOpen(true)}
                className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm font-medium"
              >
                {t('tenants:empty.action')}
              </button>
            )}
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {filteredTenants.map(tenant => (
              <TenantCard
                key={tenant.id}
                tenant={tenant}
                onEdit={(t) => {
                  setEditingTenant(t);
                  setIsModalOpen(true);
                }}
                onDeactivate={handleDeactivate}
                isAdmin={isAdmin}
              />
            ))}
          </div>
        )}

        {/* Modal */}
        <TenantModal
          isOpen={isModalOpen}
          onClose={() => {
            setIsModalOpen(false);
            setEditingTenant(null);
          }}
          tenant={editingTenant}
          onSave={editingTenant ? handleUpdate : handleCreate}
        />
      </div>
    </Layout>
  );
}

// Icons
function BuildingIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
    </svg>
  );
}

function MapPinIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
    </svg>
  );
}

function MailIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
    </svg>
  );
}

function PhoneIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
    </svg>
  );
}

function GlobeIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9a9 9 0 01-9-9m9 9c1.657 0 3-4.03 3-9s-1.343-9-3-9m0 18c-1.657 0-3-4.03-3-9s1.343-9 3-9m-9 9a9 9 0 019-9" />
    </svg>
  );
}

function DocumentIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
    </svg>
  );
}

function MetaIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="currentColor">
      <path d="M12 2C6.477 2 2 6.477 2 12c0 4.991 3.657 9.128 8.438 9.879V14.89h-2.54V12h2.54V9.797c0-2.506 1.492-3.89 3.777-3.89 1.094 0 2.238.195 2.238.195v2.46h-1.26c-1.243 0-1.63.771-1.63 1.562V12h2.773l-.443 2.89h-2.33v6.989C18.343 21.129 22 16.99 22 12c0-5.523-4.477-10-10-10z"/>
    </svg>
  );
}

function PlusIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
    </svg>
  );
}

function SearchIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
    </svg>
  );
}

function EyeIcon({ show }: { show: boolean }) {
  if (show) {
    return (
      <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
      </svg>
    );
  }
  return (
    <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
    </svg>
  );
}
